import type { HTMLAttributes, ReactNode } from "react";
import { classNames } from "../internal/classNames";
import { StatusGlyph } from "../internal/StatusGlyph";
import type { StatusGlyphName } from "../internal/StatusGlyph";
import "./Alert.css";

export type AlertVariant = "information" | "success" | "warning" | "danger";

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  variant?: AlertVariant;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  persistent?: boolean;
  onDismiss?: () => void;
  dismissLabel?: string;
}

const glyphs: Record<AlertVariant, StatusGlyphName> = {
  information: "information",
  success: "check",
  warning: "attention",
  danger: "close",
};

export function Alert({
  variant = "information",
  title,
  children,
  action,
  persistent = false,
  onDismiss,
  dismissLabel = "알림 닫기",
  className,
  ...props
}: AlertProps) {
  const urgent = variant === "danger" || variant === "warning";

  return (
    <div
      {...props}
      aria-live={urgent ? "assertive" : "polite"}
      className={classNames("cp-alert", `cp-alert--${variant}`, persistent && "cp-alert--persistent", className)}
      role={urgent ? "alert" : "status"}
    >
      <StatusGlyph className="cp-alert__icon" name={glyphs[variant]} />
      <div className="cp-alert__content">
        <strong className="cp-alert__title">{title}</strong>
        {children && <div className="cp-alert__description">{children}</div>}
        {action && <div className="cp-alert__action">{action}</div>}
      </div>
      {!persistent && onDismiss && (
        <button aria-label={dismissLabel} className="cp-alert__dismiss" onClick={onDismiss} type="button">
          <StatusGlyph name="close" />
        </button>
      )}
    </div>
  );
}
